import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { GoACheckbox, GoAFormItem, GoASpacer } from '@abgov/react-components';
import { Collision } from '../../models/collision';
import {
  publishCollision,
  updateCollision,
} from '../../state/slices/collisionSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { update } from 'lodash';
import { RoadSurfaceConditionsComponent } from './road-surface-conditions-component';
import useCsrService from '../../services/csr-api-redux';

interface ContributingFactorsProps extends BaseWizardProps {}

export const ContributingFactorsIntroductionComponent = ({
  navigationConfiguration,
}: ContributingFactorsProps) => {
  const stateCollision = useSelector((state: RootState) => state.collision);
  const dispatch: AppDispatch = useDispatch();
  const csrService = useCsrService();

  const errorHeaderText = 'Make a selection below';
  const errorText =
    "Check any of the conditions to continnue. If none apply, check 'None of the above'";
  const [error, setError] = useState('');
  const [nextPage, setNextPage] = useState('');
  const [selectWeatherConditions, setSelectWeatherConditions] = useState(
    stateCollision.collision?.vehicles![0].isEnvironmentalConditionAFactor == 1,
  );
  const [selectLightConditions, setSelectLightConditions] = useState(
    stateCollision.collision?.vehicles![0].isLightConditionAFactor == 1,
  );
  const [selectRoadConditions, setSelectRoadConditions] = useState(
    stateCollision.collision?.vehicles![0].isRoadConditionAFactor == 1,
  );
  const [selectTrafficControlDevices, setSelectTrafficControlDevices] =
    useState(
      stateCollision.collision?.vehicles![0].isTrafficControlDeviceAFactor == 1,
    );
  const [selectVehicleConditions, setSelectVehicleConditions] = useState(
    stateCollision.collision?.vehicles![0].isVehicleConditionAFactor == 1,
  );
  const [selectNoneOfTheAbove, setSelectNoneOfTheAbove] = useState(
    stateCollision.collision?.vehicles![0].isNoContributingFactors,
  );

  const validate = (): boolean => {
    let isValid = true;
    if (
      !selectWeatherConditions &&
      !selectLightConditions &&
      !selectRoadConditions &&
      !selectVehicleConditions &&
      !selectTrafficControlDevices &&
      !selectNoneOfTheAbove
    ) {
      setError(errorText);
      dispatch(removeAllValidationErrorNotifications());
      dispatch(
        pushNotification({
          body: errorHeaderText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      isValid = false;
    }
    return isValid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        selectNoneOfTheAbove ? true : false,
        selectNoneOfTheAbove ? false : true,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.vehicles = [
      {
        ...stateCollision?.collision?.vehicles![0],
        isEnvironmentalConditionAFactor: selectWeatherConditions ? 1 : 0,
        isLightConditionAFactor: selectLightConditions ? 1 : 0,
        isRoadConditionAFactor: selectRoadConditions ? 1 : 0,
        isTrafficControlDeviceAFactor: selectTrafficControlDevices ? 1 : 0,
        isVehicleConditionAFactor: selectVehicleConditions ? 1 : 0,
        isNoContributingFactors: selectNoneOfTheAbove,
        environmentalCondition: selectWeatherConditions
          ? updatedCollision.vehicles![0].environmentalCondition
          : null,
        lightConditionA: selectLightConditions
          ? updatedCollision.vehicles![0].lightConditionA
          : null,
        lightConditionB: selectLightConditions
          ? updatedCollision.vehicles![0].lightConditionB
          : null,
        contributingRoadCondition: selectRoadConditions
          ? updatedCollision.vehicles![0].contributingRoadCondition
          : null,
        surfaceCondition: selectRoadConditions
          ? updatedCollision.vehicles![0].surfaceCondition
          : null,
        trafficControlDevice: selectTrafficControlDevices
          ? updatedCollision.vehicles![0].trafficControlDevice
          : null,
        trafficControlCondition: selectTrafficControlDevices
          ? updatedCollision.vehicles![0].trafficControlCondition
          : null,
        vehicleCondition: selectVehicleConditions
          ? updatedCollision.vehicles![0].vehicleCondition
          : null,
      },
    ];

    dispatch(updateCollision(updatedCollision));
    dispatch(
      publishCollision({
        csrService,
      }),
    );

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const updateSelections = (
    value: boolean,
    setSelection: (value: boolean) => void,
  ) => {
    setSelectNoneOfTheAbove(false);
    setError('');
    dispatch(removeAllValidationErrorNotifications());
    if (value) setSelectNoneOfTheAbove(false);
    setSelection(value);
  };

  useEffect(() => {
    if (selectNoneOfTheAbove) {
      setNextPage(
        navigationConfiguration.pagingConfiguration.find(
          (item) => item.na !== undefined,
        )?.na ?? 'error',
      );
    } else {
      if (selectWeatherConditions) {
        setNextPage(
          navigationConfiguration.pagingConfiguration.find(
            (item) => item.weather !== undefined,
          )?.weather ?? 'error',
        );
      } else if (!selectWeatherConditions && selectLightConditions) {
        setNextPage(
          navigationConfiguration.pagingConfiguration.find(
            (item) => item.light !== undefined,
          )?.light ?? 'error',
        );
      } else if (
        !selectWeatherConditions &&
        !selectLightConditions &&
        selectRoadConditions
      ) {
        setNextPage(
          navigationConfiguration.pagingConfiguration.find(
            (item) => item.road !== undefined,
          )?.road ?? 'error',
        );
      } else if (
        !selectWeatherConditions &&
        !selectLightConditions &&
        !selectRoadConditions &&
        selectTrafficControlDevices
      ) {
        setNextPage(
          navigationConfiguration.pagingConfiguration.find(
            (item) => item.traffic !== undefined,
          )?.traffic ?? 'error',
        );
      } else if (
        !selectWeatherConditions &&
        !selectLightConditions &&
        !selectRoadConditions &&
        !selectTrafficControlDevices &&
        selectVehicleConditions
      ) {
        setNextPage(
          navigationConfiguration.pagingConfiguration.find(
            (item) => item.vehicle !== undefined,
          )?.vehicle ?? 'error',
        );
      }
    }
  }, [
    selectNoneOfTheAbove,
    selectWeatherConditions,
    selectLightConditions,
    selectRoadConditions,
    selectTrafficControlDevices,
    selectVehicleConditions,
  ]);

  return (
    <div>
      <h1>Contributing factors</h1>
      <GoASpacer vSpacing={'l'} />
      Provide additional factors that may have contributed to your collision.
      <GoASpacer vSpacing={'l'} />
      <h4>
        Select any applicable conditions below that were a contributing factor
        in your collision
      </h4>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem error={error}>
        <GoACheckbox
          checked={selectWeatherConditions}
          name="weatherConditions"
          text="Weather conditions"
          onChange={(_, value) => {
            updateSelections(value, setSelectWeatherConditions);
          }}
        />
        <GoACheckbox
          checked={selectLightConditions}
          name="light conditions"
          text="Light conditions"
          onChange={(_, value) => {
            updateSelections(value, setSelectLightConditions);
          }}
        />
        <GoACheckbox
          checked={selectRoadConditions}
          name="roadConditions"
          text="Road and surface conditions"
          description="eg. wet, snow, potholes, under construction"
          onChange={(_, value) => {
            updateSelections(value, setSelectRoadConditions);
          }}
        />

        <GoACheckbox
          checked={selectTrafficControlDevices}
          name="trafficControlDevices"
          text="Traffic control devices"
          description="eg. missing stop sign, non-functioning traffic light"
          onChange={(_, value) => {
            updateSelections(value, setSelectTrafficControlDevices);
          }}
        />
        <GoACheckbox
          checked={selectVehicleConditions}
          name="vehicleConditions"
          text="Vehicle conditions"
          description="eg. defective brakes, tire failure"
          onChange={(_, value) => {
            updateSelections(value, setSelectVehicleConditions);
          }}
        />
        <GoACheckbox
          checked={selectNoneOfTheAbove}
          name="noneOfTheAbove"
          text="None of the above"
          onChange={(_, value) => {
            updateSelections(value, setSelectNoneOfTheAbove);
            // If "none of the above" is selected, unselect all other checkboxes
            if (value) {
              setSelectWeatherConditions(false);
              setSelectLightConditions(false);
              setSelectRoadConditions(false);
              setSelectTrafficControlDevices(false);
              setSelectVehicleConditions(false);
            }
          }}
        />
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={validate}
        save={save}
        navigate={navigationConfiguration.navigate}
        goToReview={navigationConfiguration.goToReview}
      />
    </div>
  );
};
